import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { attentionApi, type TopicRow } from '@/lib/api/attention';
import { cn } from '@/lib/cn';

/**
 * Topics the class is struggling with, worst first.
 *
 * The one question a per-student queue structurally cannot answer. A topic at
 * 18% a fortnight after it was taught is a teaching signal — one thing to fix —
 * not seventy individual failures, and no amount of scrolling a student list
 * will ever surface it.
 *
 * This is also what survives from the coverage grid that was abandoned: its
 * single genuine strength was that a pale column meant a badly-taught topic.
 * Expressed as a ranked list, that insight is sortable and clickable instead of
 * something a trainer has to spot.
 */
export function TopicsTable({ batchId }: { batchId: string }) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['attention', 'topics', batchId],
    queryFn: async () => (await attentionApi.getTopics(batchId)).data,
  });

  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <span className="sr-only">Loading topics…</span>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-16 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-muted-foreground">Topics could not be loaded.</p>
        <Button variant="link" onClick={() => void refetch()} className="mt-1">
          Try again
        </Button>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="border-dashed px-4 py-10 text-center shadow-none">
        <p className="text-sm text-muted-foreground">
          No practice problems are set on this curriculum yet.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {data.map((t) => (
          <TopicRowView key={t.topic_id} topic={t} />
        ))}
      </ul>
      <p className="px-1 text-[11px] leading-relaxed text-muted-foreground/70">
        Completion counts every student against every problem in the topic, so a handful of fast
        students cannot hide a class that has not started.
      </p>
    </div>
  );
}

function TopicRowView({ topic }: { topic: TopicRow }) {
  // Amber, never red. A topic the class has not got is information about the
  // teaching plan, not a failure to alarm anyone about.
  const struggling = topic.completion_pct < 40;

  return (
    <li>
      <Card className="flex flex-wrap items-center gap-x-4 gap-y-2 p-3 shadow-none">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{topic.title}</p>
          <p className="truncate text-xs text-muted-foreground">
            {topic.module_title} · {topic.problems} problem
            {topic.problems === 1 ? '' : 's'} · {topic.students_started} student
            {topic.students_started === 1 ? '' : 's'} started
          </p>
        </div>

        <div className="flex w-40 flex-none items-center gap-2">
          <Progress
            value={Math.min(100, Math.max(0, topic.completion_pct))}
            className={cn('h-1.5 flex-1', struggling && '[&>*]:bg-amber-500')}
          />
          <span
            className={cn(
              'w-10 text-right text-xs font-semibold tabular-nums',
              struggling ? 'text-amber-700' : 'text-muted-foreground',
            )}
          >
            {Math.round(topic.completion_pct)}%
          </span>
        </div>

        {/* Only when there is something. A "0 pairs" column on every row is how a
          signal teaches people to stop looking at the column. */}
        {topic.match_pairs > 0 && (
          <Badge
            variant="outline"
            className="flex-none gap-1 border-sky-200 bg-sky-50 py-px text-sky-700"
          >
            <span aria-hidden="true">≈</span>
            {topic.match_pairs} similar {topic.match_pairs === 1 ? 'pair' : 'pairs'}
          </Badge>
        )}
      </Card>
    </li>
  );
}
